import React from "react";

import MicrophoneIcon from '../asset/icon-microphone-white.png';
import StopIcon from '../asset/icon-stop-red.png';
import MicRoundedIcon from '@mui/icons-material/MicRounded';
import StopCircleRoundedIcon from '@mui/icons-material/StopCircleRounded';

import { Button } from "@mui/material";
import { useAudioRecorder } from "../AudioRecorder";
import "./CustomSentenceRecorderControl.css";
import { BLUE, BLUE_PRESSED, INK, YELLOW, YELLOW_HOVER } from '../style/verbaScholaPalette';



type CustomSentenceRecorder = {
    recordingMinutes: number;
    recordingSeconds: number;
    initRecording: boolean;
    mediaStream: MediaStream | null;
    mediaRecorder: MediaRecorder | null;
    audio: string | null;
};

export type CustomSentenceUseRecorder = {
    recorderState: CustomSentenceRecorder;
    startRecording: () => void;
    cancelRecording: () => void;
    saveRecording: () => void;
};

type CustomSentenceRecorderControlProps = {
    recorderState: CustomSentenceRecorder;
    handlers: {
        startRecording: () => void;
        cancelRecording: () => void;
        saveRecording: () => void;
    };
    isMobile: boolean
    redesigned?: boolean;
};


export default function CustomSentenceRecorderControl({ recorderState, handlers, redesigned = false }: CustomSentenceRecorderControlProps) {
    const { recordingSeconds, initRecording } = recorderState;
    const { startRecording, saveRecording } = handlers;

    return (
        <div style={redesigned ? { width: '100%' } : undefined}>
            <div>
                {initRecording ? (
                    <Button
                        title="Stop recording"
                        variant="contained"
                        disabled={recordingSeconds === 0}
                        onClick={saveRecording}
                        sx={{
                            minHeight: '48px',
                            minWidth: '192px',
                            width: redesigned ? '100%' : undefined,
                            backgroundColor: redesigned ? BLUE : '#1E1E1E',
                            color: redesigned ? '#fff' : '#F2F7FF',
                            '&:hover': {
                                backgroundColor: redesigned ? BLUE_PRESSED : 'rgba(30, 30, 30, 0.8)'
                            },
                            '& .MuiSvgIcon-root': { color: redesigned ? '#fff' : '#F2F7FF' },
                        }}
                    >
                        {redesigned ? <StopCircleRoundedIcon sx={{ mr: 2 }} /> : <img src={StopIcon} alt="Record" style={{ width: '24px', height: '24px', marginRight: '16px' }} />}
                        Stop Recording
                    </Button>
                ) : (
                    <Button title="Start recording" variant="contained" onClick={startRecording}
                        sx={{
                            minHeight: '48px',
                            minWidth: '192px',
                            width: redesigned ? '100%' : undefined,
                            backgroundColor: redesigned ? YELLOW : '#1E1E1E',
                            color: redesigned ? INK : '#F2F7FF',
                            '&:hover': {
                                backgroundColor: redesigned ? YELLOW_HOVER : 'rgba(30, 30, 30, 0.8)'
                            },
                            '& .MuiSvgIcon-root': { color: redesigned ? INK : '#F2F7FF' },
                        }}
                    >
                        {redesigned ? <MicRoundedIcon sx={{ mr: 2 }} /> : <img src={MicrophoneIcon} alt="Record" style={{ width: '24px', height: '24px', marginRight: '16px' }} />}
                        Start Recording
                    </Button>
                )}
            </div>
        </div >
    );
}


const initialState: CustomSentenceRecorder = {
    recordingMinutes: 0,
    recordingSeconds: 0,
    initRecording: false,
    mediaStream: null,
    mediaRecorder: null,
    audio: null
};

export function useRecorder(setErrorMessage: (message: string) => void) {

    const [recorderState, setRecorderState] = React.useState<CustomSentenceRecorder>(initialState);

    const intervalRef = React.useRef<ReturnType<typeof setInterval> | null>(null);

    const { startRecording: startAudioRecording, stopRecording, isRecording } = useAudioRecorder({
        setErrorMessage,
        onStop: (audioData) => {
            const audioUrl = URL.createObjectURL(audioData);
            setRecorderState(() => ({
                ...initialState,
                audio: audioUrl,
            }));
        }
    });


    const startRecording = async () => {
        setRecorderState(prev => ({
            ...prev,
            recordingMinutes: 0,
            recordingSeconds: 0,
            initRecording: true,
        }));

        await startAudioRecording();

        intervalRef.current = setInterval(() => {
            setRecorderState(prev => {
                const { recordingSeconds, recordingMinutes } = prev;
                if (recordingMinutes >= 5) {
                    clearInterval(intervalRef.current!);
                    stopRecording();
                    return prev;
                }

                if (recordingSeconds < 59) {
                    return { ...prev, recordingSeconds: recordingSeconds + 1 };
                } else {
                    return {
                        ...prev,
                        recordingMinutes: recordingMinutes + 1,
                        recordingSeconds: 0,
                    };
                }
            });
        }, 1000);
    };

    const saveRecording = () => {
        stopRecording();
        clearInterval(intervalRef.current!);
    };

    const cancelRecording = () => {
        stopRecording();
        clearInterval(intervalRef.current!);
        setRecorderState({ ...initialState });
    };

    return {
        recorderState: {
            ...recorderState,
            initRecording: isRecording,
        },
        startRecording,
        cancelRecording,
        saveRecording,
    };
}
    // React.useEffect(() => {
    //     const MAX_RECORDER_TIME = 5;
    //     let recordingInterval: Interval = null;

    //     if (recorderState.initRecording)
    //         recordingInterval = setInterval(() => {
    //             setRecorderState((prevState: CustomSentenceRecorder) => {
    //                 if (
    //                     prevState.recordingMinutes === MAX_RECORDER_TIME &&
    //                     prevState.recordingSeconds === 0
    //                 ) {
    //                     typeof recordingInterval === "number" && clearInterval(recordingInterval);
    //                     return prevState;
    //                 }

    //                 if (prevState.recordingSeconds >= 0 && prevState.recordingSeconds < 59)
    //                     return {
    //                         ...prevState,
    //                         recordingSeconds: prevState.recordingSeconds + 1,
    //                     };
    //                 else if (prevState.recordingSeconds === 59)
    //                     return {
    //                         ...prevState,
    //                         recordingMinutes: prevState.recordingMinutes + 1,
    //                         recordingSeconds: 0,
    //                     };
    //                 else return prevState;
    //             });
    //         }, 1000);
    //     else typeof recordingInterval === "number" && clearInterval(recordingInterval);

    //     return () => {
    //         typeof recordingInterval === "number" && clearInterval(recordingInterval);
    //     };
    // });

    // React.useEffect(() => {
    //     setRecorderState((prevState) => {
    //         if (prevState.mediaStream) {
    //             const newMediaRecorder = new MediaRecorder(prevState.mediaStream);
    //             return {
    //                 ...prevState,
    //                 mediaRecorder: newMediaRecorder,
    //             };
    //         }
    //         else return prevState;
    //     });
    // }, [recorderState.mediaStream]);

    // React.useEffect(() => {
    //     const recorder = recorderState.mediaRecorder;
    //     let chunks: Blob[] = [];

    //     if (recorder && recorder.state === "inactive") {
    //         recorder.start();

    //         recorder.ondataavailable = (e: MediaRecorderEvent) => {
    //             chunks.push(e.data);
    //         };

    //         recorder.onstop = () => {
    //             const blob = new Blob(chunks, { type: recorder.mimeType });
    //             chunks = [];

    //             setRecorderState((prevState: CustomSentenceRecorder) => {
    //                 if (prevState.mediaRecorder)
    //                     return {
    //                         ...initialState,
    //                         audio: window.URL.createObjectURL(blob),
    //                     };
    //                 else return initialState;
    //             });
    //         };
    //     }

    //     return () => {
    //         if (recorder) recorder.stream.getAudioTracks().forEach((track: AudioTrack) => track.stop());
    //     };
    // }, [recorderState.mediaRecorder]);



    // async function startRecording(setRecorderState: SetRecorder) {

    //     try {
    //         const stream: MediaStream = await navigator.mediaDevices.getUserMedia({ audio: true });

    //         setRecorderState((prevState) => {
    //             return {
    //                 ...prevState,
    //                 initRecording: true,
    //                 mediaStream: stream,
    //             };
    //         });
    //     } catch (err) {
    //         console.error(err)
    //         handleErrorMessage(err, setErrorMessage, history, location);
    //     }
    // }

    // function saveRecording(recorder: any) {
    //     if (recorder.state !== "inactive") recorder.stop();
    // }

    // return {
    //     recorderState,
    //     startRecording: () => startRecording(setRecorderState),
    //     cancelRecording: () => setRecorderState(initialState),
    //     saveRecording: () => saveRecording(recorderState.mediaRecorder),
    // };
// }
